import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { graphql, navigate } from 'gatsby'
import uniqid from 'uniqid'
import {
  Block,
  Blockquote,
  ButtonList,
  Content,
  Intro,
  Image,
  Video,
  Grid,
  Card,
  Cta,
  GoalCounter,
  Form,
  Carousel,
  Features,
  Pricing,
  Review,
  Steps,
  Accordion,
  FilteredList,
  Flex,
  Icon,
  Share
} from '@components'
import {
  transformPostToCard,
  removeDuplicatesByTitle,
  configCategoriesForSelect
} from '@helpers/miscHelpers'
import HubspotForm from '../partials/components/hubspot-form'

export default class ModuleArea extends Component {
  getComponent(block, post, allPosts, location) {
    switch (block.model && block.model.apiKey) {
      case 'accordion':
        return (
          <Block
            gutters
            key={block.id}
            heading={block.heading}
            leadNode={block.leadNode}
          >
            <Accordion {...block} />
          </Block>
        )
      case 'blockquote':
        return (
          <Block gutters key={block.id}>
            <Blockquote {...block} />
          </Block>
        )
      case 'button_list':
        return (
          <Block gutters key={block.id}>
            <ButtonList {...block} />
          </Block>
        )
      case 'card_grid':
        const { cards } = block
        return (
          <Block
            key={block.id}
            heading={block.heading}
            leadNode={block.leadNode}
            introGutters
          >
            <Grid template="3-col">
              {cards.map(card => {
                return (
                  <Card
                    {...card}
                    key={`card-grid-card-${card.id}`}
                    elevated
                    clickable={false}
                  />
                )
              })}
            </Grid>
          </Block>
        )
      case 'cards_carousel':
        const config = {
          breakpoints: {
            320: {
              slidesPerView: 1
            },
            1024: {
              slidesPerView: 2
            },
            1800: {
              slidesPerView: 3
            }
          }
        }
        return (
          <Block
            introGutters
            key={block.id}
            heading={block.heading}
            leadNode={block.leadNode}
            link={block.link}
          >
            <Carousel scaleSlides config={config} id={block.id}>
              {block.cards.map((card, index) => {
                return (
                  <Card
                    key={`card-carousel-card-${card.id}-${index}`}
                    image={card.image}
                    contentNode={card.contentNode}
                    clickable={false}
                    horizontal
                    elevated
                    centreInContainer
                    minusGuttersFromWidth
                  />
                )
              })}
            </Carousel>
          </Block>
        )
      case 'content':
        return (
          <Block gutters key={block.id}>
            <Content {...block} />
          </Block>
        )
      case 'cta':
        return (
          <Block key={block.id}>
            <Cta {...block} />
          </Block>
        )
      case 'hubspot_form':
        return (
          <Block key={block.id} gutters>
            <HubspotForm {...block} />
          </Block>
        )
      case 'feature_content':
        return (
          <Block
            gutters
            key={block.id}
            heading={block.heading}
            leadNode={block.leadNode}
          >
            <Features {...block} />
          </Block>
        )
      case 'filtered_blog_list':
        const transformedPosts = []

        allPosts.length > 0 &&
          allPosts.map(post =>
            transformedPosts.push(
              transformPostToCard({
                ...post,
                wideButton: true,
                horizontal: true,
                flip: true,
                wide: true,
                clickable: false
              })
            )
          )

        const filters = []
        const allCategories = removeDuplicatesByTitle(
          allPosts.flatMap(({ categories }) => categories)
        )

        allCategories.length > 1 &&
          filters.push({
            name: 'categories',
            label: 'Filter by Category',
            placeholder: {
              text: 'Show All'
            },
            options: [...configCategoriesForSelect(allCategories)]
          })

        return (
          <Block gutters key={block.id}>
            <FilteredList
              items={transformedPosts}
              defaultQueries={new Map([])}
              filters={filters}
              location={location}
            />
          </Block>
        )
      case 'form':
        let formId

        if (block.formType === 'Ditch the Scales') formId = 'ditchTheScales'
        return (
          <Block gutters key={block.id} id={formId}>
            <Form {...block} />
          </Block>
        )
      case 'goal_counter':
        return (
          <Block gutters key={block.id}>
            <GoalCounter {...block} />
          </Block>
        )
      case 'image':
        return (
          <Block
            gutters
            key={block.id}
            heading={block.heading}
            leadNode={block.leadNode}
            link={block.link}
          >
            <Image elevated {...block} />
          </Block>
        )
      case 'image_with_art_direction':
        return (
          <Block
            introGutters
            key={block.id}
            heading={block.heading}
            leadNode={block.leadNode}
            link={block.link}
          >
            <Image elevated {...block} />
          </Block>
        )
      case 'image_with_content':
        return (
          <Block gutters key={block.id}>
            <Grid
              template={
                block.moduleStyle === 'Text Left' ? '6-5-reversed' : '6-5'
              }
            >
              <Image {...block} />
              <div>
                <h3>{block.heading}</h3>
                <Content linkAlignment="left" {...block} />
              </div>
            </Grid>
          </Block>
        )
      case 'intro':
        return (
          <Block gutters key={block.id} id="intro">
            <Intro {...block} />
          </Block>
        )
      case 'people_carousel':
        const peopleCarouselConfig = {
          breakpoints: {
            320: {
              slidesPerView: 1
            },
            1024: {
              slidesPerView: 3
            }
          },
          navigation: true
        }

        return (
          <Block
            introGutters
            key={block.id}
            heading={block.heading}
            leadNode={block.leadNode}
            link={block.link}
          >
            <Carousel
              scaleSlides
              config={peopleCarouselConfig}
              id={block.id}
              emphasiseActive
              placeNavigation={'between slides'}
            >
              {block.people.map((person, index) => {
                const image = {
                  ...person.image,
                  fluid: [
                    person.mobileImage.fluid,
                    {
                      ...person.image.fluid,
                      media: `(min-width: 1024px)`
                    }
                  ]
                }

                return (
                  <Card
                    key={`person-carousel-card-${person.id}-${index}`}
                    image={image}
                    title={person.name}
                    contentNode={person.descriptionNode}
                    link={
                      (person?.link?.link?.path || person?.link?.url) && {
                        url: person.link?.link?.path || person.link.url,
                        displayText: person.link.displayText
                      }
                    }
                    video={person.video}
                    clickable={false}
                    roundImage
                    surfaceless
                    textCentre
                    slim
                  />
                )
              })}
            </Carousel>
          </Block>
        )
      case 'posts_carousel':
        // Filter all posts by finding and returning the posts that have categories which match the categories set on the posts_carousel
        const categorisedPosts = allPosts.filter(
          post =>
            post.categories.findIndex(
              category =>
                block.categories.findIndex(
                  blockCategory => blockCategory.slug === category.slug
                ) !== -1
            ) !== -1
        )

        const postsCarouselConfig = {
          spaceBetween: 0,
          breakpoints: {
            320: {
              slidesPerView: 1
            },
            1024: {
              slidesPerView: 2
            },
            1800: {
              slidesPerView: 3
            }
          }
        }

        return (
          <Block
            introGutters
            key={block.id}
            heading={block.heading}
            leadNode={block.leadNode}
            link={block.link}
          >
            <Carousel scaleSlides config={postsCarouselConfig} id={block.id}>
              {categorisedPosts.map((post, index) => {
                const transformedPost = transformPostToCard(post)

                return (
                  <Card
                    key={`post-carousel-card-${post.id}-${index}`}
                    {...transformedPost}
                    clickable={false}
                    horizontal
                    elevated
                    centreInContainer
                    minusGuttersFromWidth
                    onClick={e => {
                      e.persist()
                      e.preventDefault()

                      navigate(`/blog/`, {
                        state: {
                          category: {
                            title: e.target.innerText,
                            slug: e.target.innerText
                              .trim()
                              .toLowerCase()
                              .replace(/ /gi, '-')
                          }
                        }
                      })
                    }}
                  />
                )
              })}
            </Carousel>
          </Block>
        )
      case 'post_grid':
        const { posts } = block
        return (
          <Block
            key={block.id}
            heading={block.heading}
            leadNode={block.leadNode}
            smallHeading
            guttersReverse
          >
            <Grid gridGap="slim">
              {posts.map(post => {
                const transformedPost = transformPostToCard(post)

                return (
                  <Card
                    {...transformedPost}
                    contentNode={null}
                    horizontal
                    flip
                    clickable={false}
                    elevated
                    key={`post-card-${post.id}`}
                  />
                )
              })}
            </Grid>
          </Block>
        )
      case 'pricing':
        return (
          <Block key={block.id}>
            <Pricing {...block} />
          </Block>
        )
      case 'reviews_carousel':
        return (
          <Block
            introGutters
            key={block.id}
            heading={block.heading}
            leadNode={block.leadNode}
            link={block.link}
          >
            <Carousel
              id={block.id}
              slim
              config={{
                initialSlide: 0,
                slidesPerView: 1,
                navigation: true,
                speed: 400
              }}
            >
              {block.reviews.map((review, index) => {
                return (
                  <Review
                    key={`reviews-carousel-review-${review.id}-${index}`}
                    {...review}
                  />
                )
              })}
            </Carousel>
          </Block>
        )
      case 'share_post':
        return post ? (
          <Block gutters key={block.id}>
            <Share {...block} title={post?.heading} url={post?.path} />
          </Block>
        ) : (
          <center>{`Nothing to share. `}</center>
        )
      case 'social_media_list':
        return (
          <Block gutters maxWidth={'slim'} key={block.id}>
            <h3>{block.heading}</h3>
            {block?.leadNode?.childMarkdownRemark?.html && (
              <Content contentNode={block.leadNode} addLastMargin />
            )}
            <Flex>
              {block.items.map(({ icon, title, url, id }) => (
                <Icon
                  image={icon}
                  title={title}
                  url={url}
                  key={`social-list-item-${id}`}
                  externalLink
                />
              ))}
            </Flex>
          </Block>
        )
      case 'steps_to_join':
        return (
          <Block
            gutters
            key={block.id}
            heading={block.heading}
            leadNode={block.leadNode}
          >
            <Steps {...block} />
          </Block>
        )
      case 'video':
        return (
          <Block
            gutters
            key={block.id}
            heading={block.heading}
            leadNode={block.leadNode}
            link={block.link}
            padding="None"
          >
            <Video {...block} />
          </Block>
        )
      case 'video_with_content':
        return (
          <Block gutters key={block.id}>
            <Grid
              template={
                block.moduleStyle === 'Text Left' ? '6-5-reversed' : '6-5'
              }
            >
              <Video {...block} />
              <div>
                <h3>{block.heading}</h3>
                <Content linkAlignment="left" {...block} />
              </div>
            </Grid>
          </Block>
        )
      default:
        return (
          <center>
            {`Module Type: `}
            <code>{block?.model?.apiKey}</code>
            {` is not supported.`}
          </center>
        )
    }
  }

  render() {
    const { modules, post, allPosts, location } = this.props

    return modules.map(block => {
      return (
        <Block
          key={uniqid()}
          padding={block.padding}
          background={block.background}
          backgroundStyle={block.backgroundStyle}
        >
          {this.getComponent(block, post, allPosts, location)}
        </Block>
      )
    })
  }
}

ModuleArea.propTypes = {
  modules: PropTypes.array.isRequired
}

export const query = graphql`
  fragment PageContentModuleArea on DatoCmsPage {
    moduleArea {
      ... on DatoCmsAccordion {
        ...Accordion
      }
      ... on DatoCmsBlockquote {
        ...Blockquote
      }
      ... on DatoCmsButtonList {
        ...ButtonList
      }
      ... on DatoCmsHubspotForm {
        id
        padding
        formId
        model {
          apiKey
        }
      }
      ... on DatoCmsCardGrid {
        ...CardGrid
      }
      ... on DatoCmsCardsCarousel {
        ...CardsCarousel
      }
      ... on DatoCmsContent {
        ...Content
      }
      ... on DatoCmsCta {
        ...Cta
      }
      ... on DatoCmsFeatureContent {
        ...FeatureContent
      }
      ... on DatoCmsFilteredBlogList {
        ...FilteredBlogList
      }
      ... on DatoCmsForm {
        ...Form
      }
      ... on DatoCmsGoalCounter {
        ...GoalCounter
      }
      ... on DatoCmsImage {
        ...Image
      }
      ... on DatoCmsImageWithArtDirection {
        ...ImageWithArtDirection
      }
      ... on DatoCmsImageWithContent {
        ...ImageWithContent
      }
      ... on DatoCmsIntro {
        ...Intro
      }
      ... on DatoCmsPeopleCarousel {
        ...PeopleCarousel
      }
      ... on DatoCmsPostsCarousel {
        ...PostsCarousel
      }
      ... on DatoCmsPricing {
        ...Pricing
      }
      ... on DatoCmsReviewsCarousel {
        ...ReviewsCarousel
      }

      ... on DatoCmsSocialMediaList {
        ...SocialList
      }
      ... on DatoCmsStepsToJoin {
        ...Steps
      }
      ... on DatoCmsVideo {
        ...Video
      }
      ... on DatoCmsVideoWithContent {
        ...VideoWithContent
      }
    }
  }

  fragment IndexPageContentModuleArea on DatoCmsHome {
    moduleArea {
      ... on DatoCmsAccordion {
        ...Accordion
      }
      ... on DatoCmsBlockquote {
        ...Blockquote
      }
      ... on DatoCmsButtonList {
        ...ButtonList
      }
      ... on DatoCmsCardGrid {
        ...CardGrid
      }
      ... on DatoCmsCardGrid {
        ...CardGrid
      }
      ... on DatoCmsCardsCarousel {
        ...CardsCarousel
      }
      ... on DatoCmsContent {
        ...Content
      }
      ... on DatoCmsCta {
        ...Cta
      }
      ... on DatoCmsFeatureContent {
        ...FeatureContent
      }
      ... on DatoCmsFilteredBlogList {
        ...FilteredBlogList
      }
      ... on DatoCmsForm {
        ...Form
      }
      ... on DatoCmsGoalCounter {
        ...GoalCounter
      }
      ... on DatoCmsImage {
        ...Image
      }
      ... on DatoCmsImageWithArtDirection {
        ...ImageWithArtDirection
      }
      ... on DatoCmsImageWithContent {
        ...ImageWithContent
      }
      ... on DatoCmsIntro {
        ...Intro
      }
      ... on DatoCmsReviewsCarousel {
        ...ReviewsCarousel
      }
      ... on DatoCmsPeopleCarousel {
        ...PeopleCarousel
      }
      ... on DatoCmsPostsCarousel {
        ...PostsCarousel
      }
      ... on DatoCmsPricing {
        ...Pricing
      }
      ... on DatoCmsSocialMediaList {
        ...SocialList
      }
      ... on DatoCmsStepsToJoin {
        ...Steps
      }
      ... on DatoCmsVideo {
        ...Video
      }
      ... on DatoCmsVideoWithContent {
        ...VideoWithContent
      }
    }
  }

  fragment PostContentModuleArea on DatoCmsPost {
    moduleArea {
      ... on DatoCmsAccordion {
        ...Accordion
      }
      ... on DatoCmsBlockquote {
        ...Blockquote
      }
      ... on DatoCmsButtonList {
        ...ButtonList
      }
      ... on DatoCmsCardGrid {
        ...CardGrid
      }
      ... on DatoCmsCardGrid {
        ...CardGrid
      }
      ... on DatoCmsCardsCarousel {
        ...CardsCarousel
      }
      ... on DatoCmsContent {
        ...Content
      }
      ... on DatoCmsCta {
        ...Cta
      }
      ... on DatoCmsImage {
        ...Image
      }
      ... on DatoCmsImageWithArtDirection {
        ...ImageWithArtDirection
      }
      ... on DatoCmsImageWithContent {
        ...ImageWithContent
      }
      ... on DatoCmsReviewsCarousel {
        ...ReviewsCarousel
      }
      ... on DatoCmsSharePost {
        ...Share
      }
      ... on DatoCmsVideo {
        ...Video
      }
      ... on DatoCmsVideoWithContent {
        ...VideoWithContent
      }
      ... on DatoCmsPostGrid {
        ...PostGrid
      }
    }
  }
`
